// eslint-disable-next-line max-classes-per-file
import { Metamodel } from '../base/model';
import { IAttribute, IModel, IValidationContext, IViolation, ModelConstants, ParameterValue, sanitizeAndFormatString, validateLinkedServicePermission } from 'shell-models-lib';
import { Activity } from './activity';
import { Pipeline } from './pipeline';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { createParameterConfigurationValidator } from 'app/model/adapters/linkedservices/parameter-config.util';

import { format } from 'app/shared/utils/string.util';
import { FabricDataPipelinesLinkedService } from '../linkedservice/fabric-data-pipelines.linkedservice';
import { AzureDataFactoryLinkedService } from '../linkedservice/azure-data-factory.linkedservice';
import { AzureSynapseAnalyticsWorkspaceLinkedService } from '../linkedservice/azure-synapse-analytics.linkedservice';
import { Copier } from 'app/model/base/copier';

export enum InvokePipelineConnectionType { FABRIC, ADF, SYNAPSE }

@Metamodel.model({
    name: ModelConstants.executePipelineActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.ExecutePipelineActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.ExecutePipelineActivityLabel)
    ],
    referable: true
})
export class ExecutePipelineActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly PIPELINEID: IAttribute;
    public static readonly PIPELINENAME: IAttribute;
    public static readonly WORKSPACEID: IAttribute;

    public isWebExtensible?: boolean = false;
    public isOldActivity?: boolean = false;
    public alwaysShowNewWebExperience?: boolean = false;

    /**
     * Defines whether activity execution will wait for the dependent pipeline execution to finish.
     */
    @Metamodel.attribute({
        display: stringResources.ExecutePipelineActivityConfigurationWaitOnCompletion
    })
    public waitOnCompletion?: boolean = true;

    @Metamodel.attribute({
        display: stringResources.Parameters,
        constraint: {
            validator: createParameterConfigurationValidator(true)
        }
    })
    public parameters?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: stringResources.Pipeline,
        reference: true,
        type: Pipeline.MODEL,
        constraint: {
            validator: ExecutePipelineActivity.validatePipeline
        }
    })
    public pipeline?: Pipeline;

    @Metamodel.attribute({
        reference: true,
        association: 'PIPELINEID',
        templatable: true,
        constraint: {
            validator: ExecutePipelineActivity.validatePipelineId
        }
    })
    public pipelineId?: string;

    @Metamodel.attribute({
        reference: true,
        association: 'PIPELINENAME',
        templatable: true,
        constraint: {
            validator: ExecutePipelineActivity.validatePipelineName
        }
    })
    public pipelineName?: string;

    @Metamodel.attribute({
        reference: true,
        association: 'WORKSPACEID',
        templatable: true
    })
    public workspaceId?: string;

    // Web Extensible Properties
    @Metamodel.attribute({
        display: stringResources.ConnectionLabel,
        reference: true,
        type: FabricDataPipelinesLinkedService.MODEL,
        constraint: {
            validator: ExecutePipelineActivity.validateConnection
        }
    })
    public fabricDataPipelinesLinkedService?: FabricDataPipelinesLinkedService;
    
    @Metamodel.attribute({
        display: stringResources.ConnectionLabel,
        reference: true,
        type: AzureDataFactoryLinkedService.MODEL,
        constraint: {
            validator: ExecutePipelineActivity.validateADFConnection
        }
    })
    public adfPipelinesLinkedService?: AzureDataFactoryLinkedService;
    
    @Metamodel.attribute({
        display: stringResources.ConnectionLabel,
        reference: true,
        type: AzureSynapseAnalyticsWorkspaceLinkedService.MODEL,
        constraint: {
            validator: ExecutePipelineActivity.validateSynapseConnection
        }
    })
    public synapseWorkspacePipelinesLinkedService?: AzureSynapseAnalyticsWorkspaceLinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    public method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST';

    public path?: string = '';

    public invokePipelineType?: InvokePipelineConnectionType = InvokePipelineConnectionType.FABRIC;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    public static async validateConnection(
        pipelineActivity: ExecutePipelineActivity,
        connection: FabricDataPipelinesLinkedService,
        violation: IViolation,
        context: IValidationContext
    ): Promise<IViolation[]> | undefined {
        if (pipelineActivity.alwaysShowNewWebExperience && pipelineActivity.invokePipelineType === InvokePipelineConnectionType.FABRIC) {
            if (!connection) {
                const violations: IViolation[] = [];
                violations.push({
                    context: connection,
                    message: () => sanitizeAndFormatString(
                        stringResources.RequiredTemplate,
                        stringResources.ConnectionLabel
                    )
                });
                return violations;
            } else { 
                return validateLinkedServicePermission(connection, context);
            }
        }
        return undefined;
    }

    public static async validatePipeline(
        pipelineActivity: ExecutePipelineActivity,
        pipeline: Pipeline,
        violation: IViolation,
        context: IValidationContext
    ): Promise<IViolation[]> | undefined {
        if (!pipelineActivity.alwaysShowNewWebExperience && !pipeline) {
            const violations: IViolation[] = [];
            violations.push({
                context: pipeline,
                message: () => sanitizeAndFormatString(
                    stringResources.RequiredTemplate,
                    stringResources.Pipeline
                )
            });
            return violations;
        }
        return undefined;
    }

    public static async validatePipelineId(
        pipelineActivity: ExecutePipelineActivity,
        pipelineId: string,
        violation: IViolation,
        context: IValidationContext
    ): Promise<IViolation[]> | undefined {
        if (pipelineActivity.alwaysShowNewWebExperience && pipelineActivity.invokePipelineType === InvokePipelineConnectionType.FABRIC && !pipelineId) {
            const violations: IViolation[] = [];
            violations.push({
                context: pipelineId,
                message: () => sanitizeAndFormatString(
                    stringResources.RequiredTemplate,
                    stringResources.Pipeline
                )
            });
            return violations;
        }
        return undefined;
    }
    
    public static async validateSynapseConnection(
        pipelineActivity: ExecutePipelineActivity,
        synapseWorkspacePipelinesLinkedService: AzureSynapseAnalyticsWorkspaceLinkedService,
        violation: IViolation,
        context: IValidationContext
    ): Promise<IViolation[]> {
        if (pipelineActivity.invokePipelineType === InvokePipelineConnectionType.SYNAPSE) {
            if (!synapseWorkspacePipelinesLinkedService) {
                const violations: IViolation[] = [];
                violations.push({
                    context: synapseWorkspacePipelinesLinkedService,
                    message: () =>
                        sanitizeAndFormatString(stringResources.RequiredTemplate, stringResources.ConnectionLabel)
                });
                return violations;
            } else {
                return validateLinkedServicePermission(synapseWorkspacePipelinesLinkedService, context);
            }
        }
        return undefined;
    }

    public static async validateADFConnection(
        pipelineActivity: ExecutePipelineActivity,
        adfPipelinesLinkedService: AzureDataFactoryLinkedService,
        violation: IViolation,
        context: IValidationContext
    ): Promise<IViolation[]> | undefined {
        if (pipelineActivity.invokePipelineType === InvokePipelineConnectionType.ADF) {
            if (!adfPipelinesLinkedService) {
                const violations: IViolation[] = [];
                violations.push({
                    context: adfPipelinesLinkedService,
                    message: () =>
                        sanitizeAndFormatString(stringResources.RequiredTemplate, stringResources.ConnectionLabel)
                });
                return violations;
            } else {
                return validateLinkedServicePermission(adfPipelinesLinkedService, context);
            }
        }
        return undefined;
    }

    public static async validatePipelineName(
        pipelineActivity: ExecutePipelineActivity,
        pipelineName: string,
        violation: IViolation,
        context: IValidationContext
    ): Promise<IViolation[]> | undefined {
        if (
            (pipelineActivity.invokePipelineType === InvokePipelineConnectionType.ADF ||
                pipelineActivity.invokePipelineType === InvokePipelineConnectionType.SYNAPSE) &&
            !pipelineName
        ) {
            const violations: IViolation[] = [];
            violations.push({
                context: pipelineName,
                message: () =>
                    sanitizeAndFormatString(
                        stringResources.RequiredTemplate,
                        stringResources.Monitoring_PipelineNameLabel
                    )
            });
            return violations;
        }
        return undefined;
    }

    public onActivityCloned(clonedActivity: ExecutePipelineActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.fabricDataPipelinesLinkedService?.parameterized) {
                clonedActivity.fabricDataPipelinesLinkedService = Copier.clone(
                    context,
                    [this.fabricDataPipelinesLinkedService],
                    undefined,
                    true
                )[0] as FabricDataPipelinesLinkedService;
            }
            if (this.adfPipelinesLinkedService?.parameterized) {
                clonedActivity.adfPipelinesLinkedService = Copier.clone(
                    context,
                    [this.adfPipelinesLinkedService],
                    undefined,
                    true
                )[0] as AzureDataFactoryLinkedService;
            }
            if (this.synapseWorkspacePipelinesLinkedService?.parameterized) {
                clonedActivity.synapseWorkspacePipelinesLinkedService = Copier.clone(
                    context,
                    [this.synapseWorkspacePipelinesLinkedService],
                    undefined,
                    true
                )[0] as AzureSynapseAnalyticsWorkspaceLinkedService;
            }
        }
    }

}

@Metamodel.model({
    name: ModelConstants.executePipelineActivityDuplicate,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.ExecutePipelineActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.ExecutePipelineActivityLabel)
    ],
    referable: true
})
export class ExecutePipelineActivityDuplicate extends ExecutePipelineActivity {
    public alwaysShowNewWebExperience?: boolean = true;
}